/***
 *
 *   TICKET STATS
 *   The maths behind the stat band on the tickets page.
 *
 *   The two duration metrics are derived from ticket history rows written by
 *   the integration webhook: `action_type: 'status'`, with
 *   `history_data.before` / `.after` holding a lowercased status-template group
 *   label ("pending", "completed", "closed" — see the server's parseStatus).
 *   Rows arrive ascending by date_created and belong to the react-query cache,
 *   so nothing here may sort or reverse them in place.
 *
 *   Both durations are averaged over a rolling 30-day window and compared to
 *   the 30 days before it, so the change chip is a real period-over-period
 *   move in a unit the chip can honestly render as a percentage.
 *
 **********/

import { findCurrStatus, TERMINAL_STATUSES } from './tickets';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const WINDOW_DAYS = 30;

/**
 * Exact fractional days between two instants, or null when `end` precedes
 * `start` or either is unparseable. An imported or clock-skewed row must be
 * discarded rather than contributing |difference| to the average.
 * @returns {number|null}
 */
export function diffInDays(start, end) {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return ms / MS_PER_DAY;
}

/**
 * A half-open [from, to) window of `length` days ending `endingDaysAgo` days
 * ago, as epoch ms. Instants rather than calendar days, so the current and
 * previous periods are exactly the same length.
 */
export function periodEnding(endingDaysAgo, length = WINDOW_DAYS, now = Date.now()) {
    const to = now - endingDaysAgo * MS_PER_DAY;
    return { from: to - length * MS_PER_DAY, to };
}

export function isInPeriod(date, period) {
    const time = new Date(date).getTime();
    return Number.isFinite(time) && time >= period.from && time < period.to;
}

export function isSameLocalDay(a, b) {
    const x = new Date(a);
    const y = new Date(b);
    return (
        x.getFullYear() === y.getFullYear() &&
        x.getMonth() === y.getMonth() &&
        x.getDate() === y.getDate()
    );
}

const isStatusChange = (history) => history?.action_type === 'status';

export const isMoveToTerminal = (history) =>
    isStatusChange(history) && TERMINAL_STATUSES.includes(history.history_data?.after);

export const isMoveOutOfPending = (history) =>
    isStatusChange(history) && history.history_data?.before === 'pending';

/** Last match without mutating `list` — the cached array must keep its order. */
export function findLast(list, predicate) {
    if (!Array.isArray(list)) return null;
    for (let i = list.length - 1; i >= 0; i--) {
        if (predicate(list[i])) return list[i];
    }
    return null;
}

export function findFirst(list, predicate) {
    return Array.isArray(list) ? list.find(predicate) || null : null;
}

export const isDone = (ticket) => TERMINAL_STATUSES.includes(findCurrStatus(ticket));

/** @returns {number|null} null for an empty sample, never NaN. */
export function average(values) {
    if (!values.length) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/**
 * The most recent move into a terminal status, if the ticket is still sitting
 * there. A ticket that was completed and then reopened has no completion.
 */
export function findCompletion(ticket) {
    if (!isDone(ticket)) return null;
    return findLast(ticket.histories, isMoveToTerminal);
}

/**
 * Average age at completion for tickets completed inside `period`. The window
 * bounds the completion event, not the ticket: a long-lived ticket finished
 * yesterday still counts, at its full age.
 * @returns {number|null} days, or null when nothing completed in the period
 */
export function averageCompletionDays(tickets, period) {
    const samples = [];

    tickets?.forEach((ticket) => {
        const completion = findCompletion(ticket);
        if (!completion || !isInPeriod(completion.date_created, period)) return;

        const days = diffInDays(ticket.date_created, completion.date_created);
        if (days !== null) samples.push(days);
    });

    return average(samples);
}

/**
 * Average time from creation to the FIRST exit from pending, for exits inside
 * `period`. Tickets are created in pending (the server picks the category's
 * first pending status), so the first exit closes the initial pending spell.
 * Taking the last exit instead folds every later in_progress -> pending ->
 * in_progress round trip into one span.
 * @returns {number|null} days, or null when nothing left pending in the period
 */
export function averagePendingDays(tickets, period) {
    const samples = [];

    tickets?.forEach((ticket) => {
        const exit = findFirst(ticket.histories, isMoveOutOfPending);
        if (!exit || !isInPeriod(exit.date_created, period)) return;

        const days = diffInDays(ticket.date_created, exit.date_created);
        if (days !== null) samples.push(days);
    });

    return average(samples);
}

/**
 * Period-over-period move as a percentage of the previous period — the unit the
 * Stat chip renders. Null when either period has no sample, so a missing
 * comparison shows nothing rather than reading as "no change".
 * @returns {number|null}
 */
export function percentChange(current, previous) {
    if (current === null || previous === null || previous === 0) return null;
    return ((current - previous) / previous) * 100;
}

/**
 * Both duration tiles and their change chips, from one pass over the tickets.
 * @returns {{ completion: number|null, pending: number|null,
 *             completionChange: number|null, pendingChange: number|null }}
 */
export function calcDurationStats(tickets, now = Date.now()) {
    const current = periodEnding(0, WINDOW_DAYS, now);
    const previous = periodEnding(WINDOW_DAYS, WINDOW_DAYS, now);

    const completion = averageCompletionDays(tickets, current);
    const pending = averagePendingDays(tickets, current);

    return {
        completion,
        pending,
        completionChange: percentChange(completion, averageCompletionDays(tickets, previous)),
        pendingChange: percentChange(pending, averagePendingDays(tickets, previous)),
    };
}

/** Tickets opened today, whether or not they are still open. */
export function findNewTickets(tickets, now = Date.now()) {
    return tickets?.filter(
        (ticket) => !ticket.is_archived && isSameLocalDay(now, ticket.date_created),
    );
}

/** Tickets that are done and became done today. */
export function findCompletedToday(tickets, now = Date.now()) {
    return tickets?.filter((ticket) => {
        if (ticket.is_archived) return false;

        const completion = findCompletion(ticket);
        return !!completion && isSameLocalDay(now, completion.date_created);
    });
}
